import { useCallback, useLayoutEffect, useRef } from 'react'

import { useIsMountedRef } from './useIsMountedRef'

/**
 * Creates an asynchronous callback. Works like `useCallback`.
 * Use this hook to prevent warnings: `Can't perform a React state update on
 * an unmounted component.`
 *
 *
 * @example
 *  const [data, setData] = useState()
 *  const [error, setError] = useState()
 *  const [loading, setLoading] = useState(false)
 *
 *  const apiCall = useAsyncCallback(
 *    () =>
 *      getApi({
 *        endpoint: 'path/to/api',
 *        params: { param: paramFromProps }
 *      }),
 *    [paramFromProps]
 *  )
 *
 *  const handleClick = useCallback(() => {
 *    setLoading(true)
 *
 *    apiCall()
 *      // None of the following callbacks will be called if component unmounts
 *      // during request invocation
 *      .then(setData)
 *      .catch(setError)
 *      .finally(() => setLoading(false))
 *  }, [apiCall])
 *
 * The returned callback has a stable identity for the lifetime of the
 * component and always invokes the latest `callback` (via a ref), so it is safe
 * to use as a prop, effect dependency, or inside compiler-memoized values
 * without a dependency list. The `dependencies` argument is no longer needed.
 *
 * @param callback - Callback function
 */
export function useAsyncCallback(callback) {
  const isMountedRef = useIsMountedRef()
  const callbackRef = useRef(callback)

  useLayoutEffect(() => {
    callbackRef.current = callback
  })

  return useCallback(
    (...args) =>
      new Promise((resolve, reject) => {
        if (isMountedRef.current !== false) {
          callbackRef
            .current(...args)
            .then(result => {
              if (isMountedRef.current && result !== undefined) {
                resolve(result)
              }
            })
            .catch(error => {
              if (isMountedRef.current) {
                reject(error)
              }
            })
        }
      }),
    [isMountedRef]
  )
}
